import { Box, type BoxProps, Typography } from "@mui/material";
import { buildPasswordRequirementListItems } from "@okta/okta-signin-widget/src/v3/src/transformer/password/passwordSettingsUtils";
import { validatePassword } from "@okta/okta-signin-widget/src/v3/src/util/passwordUtils";
import React, { useEffect, useMemo } from "react";
import { Trans, useTranslation } from "react-i18next";

import { type PasswordComplexity } from "src/utils";

import { OktaPasswordStrengthIndicatorListItem } from "../OktaPasswordStrengthIndicatorListItem";

const PASSWORD_HISTORY_COUNT = 4;

interface Props extends BoxProps {
  password: string;
  shouldShowResult: boolean;
  complexity?: PasswordComplexity;
  onValidationChange: (isValid: boolean) => void;
}

export const OktaPasswordStrengthIndicatorList = ({
  password,
  shouldShowResult,
  complexity,
  onValidationChange,
  ...boxProps
}: Props) => {
  const { t } = useTranslation();

  const requirementItems = useMemo(() => buildPasswordRequirementListItems({ complexity }), [
    complexity,
  ]);

  const { validationItems, isValid } = useMemo(() => {
    const validationItems = validatePassword(password, {}, { complexity });

    return {
      validationItems,
      isValid: Object.values(validationItems).every((isValid) => isValid),
    };
  }, [password, complexity]);

  useEffect(() => {
    onValidationChange(isValid);
  }, [isValid, onValidationChange]);

  return (
    <Box {...boxProps} sx={{ p: 2, backgroundColor: "primary.light", ...boxProps.sx }}>
      <Box id="password-strength-indicator">
        <Typography>
          <Trans
            i18nKey="Pour la <b>sécurité de votre compte</b>, votre mot de passe doit contenir :"
            components={{ b: <b /> }}
          />
        </Typography>
        {requirementItems.map((requirementItem) => (
          <OktaPasswordStrengthIndicatorListItem
            key={requirementItem.ruleKey}
            {...requirementItem}
            isValid={Boolean(validationItems[requirementItem.ruleKey])}
            shouldShowResult={shouldShowResult}
          />
        ))}
        <OktaPasswordStrengthIndicatorListItem
          ruleKey="historyCount"
          label={t("Le mot de passe doit être différent de vos {{count}} anciens mots de passe", {
            count: PASSWORD_HISTORY_COUNT,
          })}
          isValid={false}
          shouldShowResult={false}
        />
      </Box>
    </Box>
  );
};
